import { useState } from "react";
import { Segmented } from "antd";
import EnrollmentComparisonChart from "./EnrollmentComparisonChart";
import EnrollmentGapChart from "./EnrollmentGapChart";
import SiteActivationRamp from "./SiteActivationRamp";
import MonthlyFunnelChart from "./MonthlyFunnelChart";
import ScreenFailRateChart from "./ScreenFailRateChart";
import EnrollmentPaceChart from "./EnrollmentPaceChart";
import PlotDownloadMenu from "./PlotDownloadMenu";
import DataTableView from "./DataTableView";
import { COLORS, SCENARIOS } from "./chartBase";
import type {
  CardHeadProps,
  ChartCardProps,
  KpisProps,
  LegendProps,
  OnboardingDashboardProps,
  OnboardingDataColumn,
  OnboardingMonthRow,
} from "./onboardingPlots.types";

const CARD = "bg-white rounded-lg shadow-omni p-4";

function formatCount(value: number | null | undefined) {
  return value == null ? "–" : `${value}`;
}

function formatPercent(value: number | null | undefined) {
  return value == null ? "–" : `${Math.round(value * 100)}%`;
}

function formatRatio(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
) {
  if (numerator == null || denominator == null || denominator === 0) return "–";
  return `${Math.round((numerator / denominator) * 100)}%`;
}

function Legend({ items }: LegendProps) {
  return (
    <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#6b8089]">
      {items.map((i) => (
        <span key={i.label} className="flex items-center gap-1.5">
          <span
            className="inline-block w-4 h-[3px] rounded"
            style={{
              background: i.dash
                ? `repeating-linear-gradient(90deg, ${i.color} 0 6px, transparent 6px 10px)`
                : i.color,
            }}
          />
          {i.label}
        </span>
      ))}
    </div>
  );
}

function CardHead({ title, hint, menu }: CardHeadProps) {
  return (
    <div className="flex items-baseline justify-between mb-1">
      <h3 className="text-[15px] font-semibold text-[#2B4752] m-0">{title}</h3>
      <div className="flex items-center gap-2">
        {hint ? <span className="text-xs text-[#6b8089]">{hint}</span> : null}
        {menu}
      </div>
    </div>
  );
}

function ChartCard({
  containerId,
  title,
  hint,
  legendItems,
  className,
  children,
  rows,
  dataColumns,
}: ChartCardProps) {
  const [view, setView] = useState<"chart" | "data">("chart");
  const canShowData = !!rows?.length && !!dataColumns?.length;

  return (
    <div className={className ?? CARD}>
      <CardHead
        title={title}
        hint={hint}
        menu={
          <div className="flex items-center gap-2">
            {canShowData ? (
              <Segmented
                size="small"
                value={view}
                onChange={(value) => setView(value as "chart" | "data")}
                options={[
                  { label: "Chart", value: "chart" },
                  { label: "Data", value: "data" },
                ]}
              />
            ) : null}
            {view === "chart" ? (
              <PlotDownloadMenu
                containerId={containerId}
                title={title}
                hint={hint}
                legendItems={legendItems}
              />
            ) : null}
          </div>
        }
      />
      {view === "data" && canShowData ? (
        <DataTableView
          rows={rows as OnboardingMonthRow[]}
          columns={dataColumns as OnboardingDataColumn[]}
        />
      ) : (
        <div id={containerId}>{children}</div>
      )}
      {view === "chart" && legendItems?.length ? (
        <Legend items={legendItems} />
      ) : null}
    </div>
  );
}

function Kpis({ rows }: KpisProps) {
  const last = [...rows].reverse().find((r) => r.enrolledCumulative != null);
  if (!last) return null;
  const active = last.totalActiveSites;
  const contracted = last.contractedActiveSites;
  const target = last.scenarios.target.enrolledCum;
  const enrolled = last.enrolledCumulative;
  const gap =
    enrolled != null && target != null ? Math.round(enrolled - target) : null;
  const cards = [
    {
      l: "Active Sites",
      v: (
        <>
          {formatCount(active)}{" "}
          <span className="text-[16px] font-normal text-[#A5A5A5]">
            / {formatCount(contracted)}
          </span>
        </>
      ),
      f: `${formatRatio(active, contracted)} of contracted`,
    },
    {
      l: "Screened (cum)",
      v: formatCount(last.screenedCumulative),
      f: "to date",
    },
    {
      l: "Screen Fail Rate",
      v: formatPercent(last.screenFailRateCumulative),
      f: `${formatCount(last.screenFailsCumulative)} of ${formatCount(last.screenedCumulative)}`,
    },
    { l: "Enrolled (cum)", v: formatCount(enrolled), f: "randomized" },
    {
      l: "vs Target plan",
      v: gap == null ? "–" : `${gap >= 0 ? "+" : ""}${gap}`,
      f: `${formatRatio(enrolled, target)} of Target`,
      tone:
        gap == null
          ? "text-[#2B4752]"
          : gap < 0
            ? "text-[#980000]"
            : "text-[#1f9d63]",
    },
    {
      l: "Enrolled / Site",
      v:
        enrolled != null && active != null && active !== 0
          ? (enrolled / active).toFixed(2)
          : "–",
      f: "cumulative avg",
    },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
      {cards.map((c) => (
        <div key={c.l} className={CARD}>
          <div className="text-[12px] text-[#6b8089] font-medium">{c.l}</div>
          <div
            className={`text-[26px] font-bold mt-1 leading-none ${c.tone ?? "text-[#2B4752]"}`}
          >
            {c.v}
          </div>
          <div className="text-[12px] text-[#6b8089] mt-1">{c.f}</div>
        </div>
      ))}
    </div>
  );
}

export default function OnboardingDashboard({
  rows,
}: OnboardingDashboardProps) {
  const hasExplicitForecast = rows.some(
    (row) =>
      row.forecastEnrolledCumulativeP10 != null ||
      row.forecastEnrolledCumulativeP50 != null ||
      row.forecastEnrolledCumulativeP90 != null,
  );
  const scenarioLegend = [
    { color: COLORS.actual, label: "Actual" },
    ...SCENARIOS.map((s) => ({ color: s.color, label: s.label, dash: true })),
  ];
  const enrollmentLegend = hasExplicitForecast
    ? [
        { color: COLORS.actual, label: "Actual" },
        { color: COLORS.forecast, label: "Forecast" },
        ...SCENARIOS.map((s) => ({
          color: s.color,
          label: s.label,
          dash: true,
        })),
      ]
    : scenarioLegend;
  const siteActivationLegend = [
    { color: COLORS.actual, label: "Active (Actual)" },
    { color: "#C9922B", label: "Contracted (Plan)", dash: true },
  ];
  const monthlyFunnelLegend = [
    { color: COLORS.screenedBar, label: "Screened" },
    { color: COLORS.red, label: "Screen Fails" },
    { color: COLORS.actual, label: "Enrolled" },
  ];
  const screenFailRateLegend = [
    { color: COLORS.sfBar, label: "Monthly SF rate" },
    { color: COLORS.red, label: "Cumulative SF rate" },
  ];
  const enrollmentPaceLegend = [
    { color: COLORS.paceBar, label: "Actual enroll / site / month" },
    ...SCENARIOS.map((s) => ({
      color: s.color,
      label: String(s.rate),
      dash: true,
    })),
  ];

  // Keeps the S-curve's "view as data" table in step with the forecast fan.
  const forecastColumns: OnboardingDataColumn[] = hasExplicitForecast
    ? [
        {
          label: "Forecast (p50)",
          get: (r: OnboardingMonthRow) =>
            r.forecastEnrolledCumulativeP50 ?? null,
        },
        {
          label: "Forecast (p10)",
          get: (r: OnboardingMonthRow) =>
            r.forecastEnrolledCumulativeP10 ?? null,
        },
        {
          label: "Forecast (p90)",
          get: (r: OnboardingMonthRow) =>
            r.forecastEnrolledCumulativeP90 ?? null,
        },
      ]
    : [];
  const enrollmentScurveColumns: OnboardingDataColumn[] = [
    { label: "Actual", get: (r) => r.enrolledCumulative },
    ...forecastColumns,
    ...SCENARIOS.map((s) => ({
      label: s.label,
      get: (r: OnboardingMonthRow) => r.scenarios[s.key].enrolledCum,
    })),
  ];
  const screeningScurveColumns: OnboardingDataColumn[] = [
    { label: "Actual", get: (r) => r.screenedCumulative },
    ...SCENARIOS.map((s) => ({
      label: s.label,
      get: (r: OnboardingMonthRow) => r.scenarios[s.key].screenedCum,
    })),
  ];
  const enrollmentGapColumns: OnboardingDataColumn[] = [
    { label: "Actual Enrolled (cum)", get: (r) => r.enrolledCumulative },
    { label: "Target (cum)", get: (r) => r.scenarios.target.enrolledCum },
    {
      label: "Gap",
      get: (r) =>
        r.enrolledCumulative != null && r.scenarios.target.enrolledCum != null
          ? r.enrolledCumulative - r.scenarios.target.enrolledCum
          : null,
    },
  ];
  const siteActivationColumns: OnboardingDataColumn[] = [
    { label: "Active Sites", get: (r) => r.totalActiveSites },
    { label: "Contracted Sites", get: (r) => r.contractedActiveSites },
  ];
  const monthlyFunnelColumns: OnboardingDataColumn[] = [
    { label: "Screened", get: (r) => r.screenedPerMonth },
    { label: "Screen Fails", get: (r) => r.screenFailsPerMonth },
    { label: "Enrolled", get: (r) => r.enrolledPerMonth },
  ];
  const screenFailRateColumns: OnboardingDataColumn[] = [
    {
      label: "Monthly SF Rate",
      get: (r) => r.screenFailRatePerMonth,
      percent: true,
    },
    {
      label: "Cumulative SF Rate",
      get: (r) => r.screenFailRateCumulative,
      percent: true,
    },
  ];
  const enrollmentPaceColumns: OnboardingDataColumn[] = [
    {
      label: "Enrolled / Site / Month",
      get: (r) => r.enrolledPerSitePerMonth,
      decimals: 2,
    },
  ];

  return (
    <div className="w-full">
      <Kpis rows={rows} />

      <ChartCard
        containerId="chart-enrollment-scurve"
        title="Cumulative Enrollment S-Curve"
        hint="randomized subjects"
        legendItems={enrollmentLegend}
        className={`${CARD} mb-4`}
        rows={rows}
        dataColumns={enrollmentScurveColumns}
      >
        <EnrollmentComparisonChart rows={rows} metric="enrolled" height={380} />
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          containerId="chart-enrollment-gap"
          title="Enrollment Gap vs Target"
          hint="actual − Target (0.7)"
          rows={rows}
          dataColumns={enrollmentGapColumns}
        >
          <EnrollmentGapChart rows={rows} />
        </ChartCard>
        <ChartCard
          containerId="chart-screening-scurve"
          title="Cumulative Screening S-Curve"
          hint="subjects screened"
          legendItems={scenarioLegend}
          rows={rows}
          dataColumns={screeningScurveColumns}
        >
          <EnrollmentComparisonChart
            rows={rows}
            metric="screened"
            height={300}
            sparseXTicks
          />
        </ChartCard>
        <ChartCard
          containerId="chart-site-activation"
          title="Site Activation Ramp"
          hint="active vs contracted"
          legendItems={siteActivationLegend}
          rows={rows}
          dataColumns={siteActivationColumns}
        >
          <SiteActivationRamp rows={rows} />
        </ChartCard>
        <ChartCard
          containerId="chart-monthly-funnel"
          title="Monthly Screening Funnel"
          hint="per month"
          legendItems={monthlyFunnelLegend}
          rows={rows}
          dataColumns={monthlyFunnelColumns}
        >
          <MonthlyFunnelChart rows={rows} />
        </ChartCard>
        <ChartCard
          containerId="chart-screen-fail-rate"
          title="Screen Fail Rate"
          hint="monthly & cumulative"
          legendItems={screenFailRateLegend}
          rows={rows}
          dataColumns={screenFailRateColumns}
        >
          <ScreenFailRateChart rows={rows} assumption={0.5} />
        </ChartCard>
        <ChartCard
          containerId="chart-enrollment-pace"
          title="Enrollment Pace per Site per Month"
          hint="actual vs scenario rate targets"
          legendItems={enrollmentPaceLegend}
          className={`${CARD} lg:col-span-2`}
          rows={rows}
          dataColumns={enrollmentPaceColumns}
        >
          <EnrollmentPaceChart rows={rows} />
        </ChartCard>
      </div>
    </div>
  );
}
